import StatusBadge from '@/components/base/StatusBadge';
import type { GossipEvent, TimelineKind } from '@/types/gossip';

interface FollowUpZoneProps {
  featured: GossipEvent;
  feed: GossipEvent[];
}

const kindStyles: Record<TimelineKind, string> = {
  起因: 'bg-secondary-500',
  发酵: 'bg-secondary-400',
  反转: 'bg-primary-500',
  回应: 'bg-accent-500',
  最新: 'bg-primary-600',
};

const kindText: Record<TimelineKind, string> = {
  起因: 'text-secondary-700 bg-secondary-100 dark:bg-secondary-900 dark:text-secondary-100',
  发酵: 'text-secondary-700 bg-secondary-100 dark:bg-secondary-900 dark:text-secondary-100',
  反转: 'text-primary-700 bg-primary-100 dark:bg-primary-900 dark:text-primary-200',
  回应: 'text-accent-900 bg-accent-100 dark:bg-accent-900 dark:text-accent-100',
  最新: 'text-primary-800 bg-primary-200 dark:bg-primary-900 dark:text-primary-100',
};

export default function FollowUpZone({ featured, feed }: FollowUpZoneProps) {
  return (
    <section id="followup" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50 scroll-mt-20">
      <div className="max-w-3xl">
        <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 flex items-center gap-2">
          持续跟进专区
          <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-label text-primary-700 align-middle">
            <i className="ri-loop-right-line"></i>
            不断更新
          </span>
        </h2>
        <p className="mt-2 text-sm text-foreground-600">
          很多瓜不是一次就能吃透，后面随时会反转、会有新回应。我们把最新回应、后续进展、反转更新直接汇总出来，有头有尾地摆着看。
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
        <div className="lg:col-span-2 rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-7">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-label text-background-50 dark:text-foreground-950">
              跟进样本
            </span>
            <StatusBadge status={featured.status} />
            <span className="text-xs text-foreground-500">已更新 {featured.updateCount} 次</span>
          </div>

          <h3 className="mt-3 font-heading text-xl md:text-2xl text-foreground-950">{featured.title}</h3>

          <ol className="mt-6 relative border-l-2 border-background-200/80 ml-2">
            {featured.timeline.map((item, index) => (
              <li key={`${item.time}-${index}`} className="relative pl-6 pb-6 last:pb-0">
                <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full ring-4 ring-background-100 ${kindStyles[item.kind] || 'bg-secondary-500'}`}></span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded px-2 py-0.5 text-[11px] font-label ${kindText[item.kind] || kindText.发酵}`}>
                    {item.kind}
                  </span>
                  <span className="text-xs text-foreground-500">{item.time}</span>
                </div>
                <p className="mt-1.5 text-sm text-foreground-800 leading-relaxed">{item.text}</p>
              </li>
            ))}
          </ol>

          <a
            href={`#${featured.id}`}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            追这条瓜的完整时间线
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        <div className="rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg text-foreground-950 flex items-center gap-2">
              <i className="ri-chat-smile-2-line text-accent-600"></i>
              最新回应
            </h3>
            <span className="text-xs text-foreground-500">实时汇总</span>
          </div>

          <div className="mt-4 flex flex-col gap-4">
            {feed.map((event) => (
              <a key={event.id} href={`#${event.id}`} className="group block border-b border-background-200/60 pb-4 last:border-0 last:pb-0 cursor-pointer">
                <div className="flex items-center gap-2">
                  <StatusBadge status={event.status} />
                  <span className="text-xs text-foreground-500">{event.latestTime}</span>
                </div>
                <p className="mt-2 text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {event.title}
                </p>
                <p className="mt-1 text-xs text-foreground-600 leading-relaxed line-clamp-2">{event.latestText}</p>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}